import { isFormControlDataType, type FormFieldModel, type FormControlDataType, FormControlConstants } from '@generated';
import type { FormValues } from '~/shared/forms/types';
import { formatToShortDateAndTimeOrNull, formatToShortDateOrNull, formatToShortTime } from '~/util/date';

export function getInitialValues(formFields: FormFieldModel[], values?: Record<string, string>): FormValues | null {
	if (!formFields || formFields.length === 0) {
		return null;
	}
	const initialValues = formFields.reduce((current: FormValues, formField: FormFieldModel) => {
		const fieldName = formField.name;
		if (formField.defaultValue !== undefined) {
			current[fieldName] = formField.defaultValue;
			return current;
		}

		if (!isFormControlDataType(formField.dataType)) {
			throw new Error(`Field ${fieldName} has an invalid data type`);
		}
		const dataType = formField.dataType as unknown as FormControlDataType;
		if (dataType === FormControlConstants.formControlDataTypes.boolean) {
			current[fieldName] = false;
		} else if (
			dataType === FormControlConstants.formControlDataTypes.stringArray ||
			dataType === FormControlConstants.formControlDataTypes.objectArray
		) {
			current[fieldName] = [];
		} else {
			current[fieldName] = null;
		}
		return current;
	}, {});

	if (!values) {
		return initialValues;
	}

	return { ...initialValues, ...convertServerValuesToClientValues(formFields, values) };
}

export function getFormFieldDefaultValue(field: FormFieldModel): unknown {
	if (field.defaultValue !== undefined) {
		if (
			field.dataType === FormControlConstants.formControlDataTypes.integer ||
			field.dataType === FormControlConstants.formControlDataTypes.decimal
		) {
			return Number(field.defaultValue);
		}
		return field.defaultValue;
	}

	if (!isFormControlDataType(field.dataType)) {
		throw new Error(`Field ${field.name} has an invalid data type`);
	}
	const dataType = field.dataType as unknown as FormControlDataType;
	if (dataType === FormControlConstants.formControlDataTypes.boolean) {
		return false;
	} else if (
		dataType === FormControlConstants.formControlDataTypes.stringArray ||
		dataType === FormControlConstants.formControlDataTypes.objectArray
	) {
		return [];
	} else {
		return null;
	}
}

export function convertClientValuesToServerValues(formValues: FormValues, formFields: FormFieldModel[]): Record<string, string> | undefined {
	if (!formFields || formFields.length === 0) {
		return undefined;
	}
	return formFields.reduce((acc: Record<string, string>, field) => {
		const fieldName = field.name;
		const value = formValues[fieldName];
		if (isEmpty(value)) {
			return acc;
		}
		if (!isFormControlDataType(field.dataType)) {
			throw new Error(`Field ${fieldName} has an invalid data type`);
		}
		const dataType = field.dataType as unknown as FormControlDataType;
		switch (dataType) {
			case FormControlConstants.formControlDataTypes.date:
			case FormControlConstants.formControlDataTypes.dateTime:
			case FormControlConstants.formControlDataTypes.time:
				return {
					...acc,
					[fieldName]: value.toISOString()
				};
			case FormControlConstants.formControlDataTypes.boolean:
				return {
					...acc,
					[fieldName]: String(value)
				};
			case FormControlConstants.formControlDataTypes.stringArray:
				return {
					...acc,
					[fieldName]: value.join(',')
				};
			case FormControlConstants.formControlDataTypes.objectArray:
				return {
					...acc,
					[fieldName]: JSON.stringify(value)
				};
			case FormControlConstants.formControlDataTypes.integer:
			case FormControlConstants.formControlDataTypes.decimal:
				return {
					...acc,
					[fieldName]: String(value)
				};
			default:
				return {
					...acc,
					[fieldName]: value.toString()
				};
		}
	}, {});
}

function convertServerValuesToClientValues(formFields: FormFieldModel[], values?: Record<string, string>): FormValues {
	if (!values) {
		return {};
	}
	if (!formFields) {
		throw new Error('Documentation form fields are missing');
	}
	return Object.entries(values).reduce((acc: FormValues, [key, value]) => {
		if (isEmpty(value)) {
			return {
				...acc,
				[key]: value
			};
		}
		const field = formFields.find(field => field.name === key);
		if (!field) {
			throw new Error(`Field ${key} not found in form fields`);
		}
		const fieldName = field.name;
		if (!isFormControlDataType(field.dataType)) {
			throw new Error(`Field ${fieldName} has an invalid data type`);
		}
		const dataType = field.dataType as unknown as FormControlDataType;
		switch (dataType) {
			case FormControlConstants.formControlDataTypes.date:
			case FormControlConstants.formControlDataTypes.dateTime:
			case FormControlConstants.formControlDataTypes.time:
				return {
					...acc,
					[fieldName]: new Date(value)
				};
			case FormControlConstants.formControlDataTypes.boolean:
				return {
					...acc,
					[fieldName]: value === 'true'
				};
			case FormControlConstants.formControlDataTypes.stringArray:
				return {
					...acc,
					[fieldName]: value.split(',')
				};
			case FormControlConstants.formControlDataTypes.objectArray:
				return {
					...acc,
					[fieldName]: JSON.parse(value)
				};
			case FormControlConstants.formControlDataTypes.integer:
			case FormControlConstants.formControlDataTypes.decimal:
				return {
					...acc,
					[fieldName]: Number(value)
				};
			default:
				return {
					...acc,
					[fieldName]: value
				};
		}
	}, {});
}

export function convertServerValuesToClientReadonlyValues(
	formFields?: FormFieldModel[],
	values?: Record<string, string>
): Record<string, string | string[] | null> {
	if (!formFields || formFields.length === 0 || !values) {
		return {};
	}
	return Object.entries(values).reduce((acc: FormValues, [key, value]) => {
		if (isEmpty(value)) {
			return {
				...acc,
				[key]: value
			};
		}
		const field = formFields.find(field => field.name === key);
		if (!field) {
			throw new Error(`Field ${key} not found in form fields`);
		}
		const fieldName = field.name;
		if (!isFormControlDataType(field.dataType)) {
			throw new Error(`Field ${fieldName} has an invalid data type`);
		}
		const dataType = field.dataType as unknown as FormControlDataType;
		switch (dataType) {
			case FormControlConstants.formControlDataTypes.date:
				return {
					...acc,
					[fieldName]: formatToShortDateOrNull(new Date(value))
				};
			case FormControlConstants.formControlDataTypes.dateTime:
				return {
					...acc,
					[fieldName]: formatToShortDateAndTimeOrNull(new Date(value))
				};
			case FormControlConstants.formControlDataTypes.time:
				return {
					...acc,
					[fieldName]: formatToShortTime(new Date(value))
				};
			case FormControlConstants.formControlDataTypes.boolean:
				return {
					...acc,
					[fieldName]: value === 'true' ? 'Yes' : 'No'
				};
			case FormControlConstants.formControlDataTypes.stringArray:
				return {
					...acc,
					[fieldName]: value.split(',')
				};
			case FormControlConstants.formControlDataTypes.objectArray:
				return {
					...acc,
					[fieldName]: JSON.parse(value)
				};
			default:
				return {
					...acc,
					[fieldName]: value
				};
		}
	}, {});
}

export function isEmpty(value: unknown) {
	if (value === null || value === undefined) return true;
	if (value instanceof Date) return false;
	if (typeof value === 'string' || Array.isArray(value)) return value.length === 0;
	if (typeof value === 'object') return Object.keys(value).length === 0;
	return false;
}
